import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { addEntity, EntityProps, EntityState, removeEntity, setAllEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { InterrelationService } from '../interrelations.service';
import { Interrelation } from '../models/interrelation.model';
import { InterrelationCreateRequest } from '../requests/create-request';
import { InterrelationUpdateRequest } from '../requests/update-request';

export function avecMéthodesInterrelations<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<Interrelation>>(),
      props: type<EntityProps<Interrelation>>(),
    },
    // Updater
    withMethods((store) => ({
      chargerInterrelations: (state: { interrelations: Interrelation[] }) =>
        patchState(store, setAllEntities(state.interrelations)),
      ajouterInterrelation: (state: { interrelation: Interrelation }) =>
        patchState(store, addEntity(state.interrelation)),
      supprimerInterrelation: (nom: string) => patchState(store, removeEntity(nom)),
    })),

    //Effects
    withMethods((store, interrelationService = inject(InterrelationService)) => ({
      chargerToutes: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            interrelationService.findAll(idCartographie).pipe(
              tapResponse({
                next: (result) =>
                  store.chargerInterrelations({
                    interrelations: result.items,
                  }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des interrelations, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{ data: InterrelationCreateRequest; next?: () => void }>(
        pipe(
          switchMap(({ data, next }) =>
            interrelationService.créer(data).pipe(
              tapResponse({
                next: () => next?.(),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la création de l'interrelation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        data: InterrelationUpdateRequest;
        next: () => void;
      }>(
        pipe(
          switchMap(({ data, next }) =>
            interrelationService.modifier(data).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification de l'interrelation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{ idInterrelation: string; next: () => void }>(
        pipe(
          switchMap(({ idInterrelation, next }) =>
            interrelationService.supprimer(idInterrelation).pipe(
              tapResponse({
                next: () => {
                  store.supprimerInterrelation(idInterrelation);
                  next();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression de l'interrelation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      selection: rxMethod<string>(
        pipe(
          switchMap((idInterrelation) => interrelationService.findById(idInterrelation)),
          tapResponse({
            next: (interrelation) => store.ajouterInterrelation({ interrelation }),
            error: (err) => {
              console.error(err);
              const toastrId = uuidv7();
              toast.custom(ToastrErrorComponent, {
                componentProps: {
                  message: `Un problème est survenu lors de la récupération de l'interrelation, contactez le support.`,
                  id: toastrId,
                },
                id: toastrId,
              });
            },
          }),
        ),
      ),
      parPhase: (idPhase: string): Interrelation[] => store.entities().filter((i) => i.idsPhase?.includes(idPhase)),
    })),
  );
}
